import { Component, DoCheck, EventEmitter, HostListener, Output, ChangeDetectionStrategy } from '@angular/core'
import { Subscription } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { ReportType, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { timeAgoString } from 'src/app/shared/utils'
import { emailAddressIsValid } from 'src/utils/validators'
import { AccountService } from '../../services/account.service'
import { Application, ApplicationProviderService } from '../../services/application.service'
import { SyncPairProviderService } from '../../services/sync-pair.service'
import { LaunchRestoreSettings, LaunchSyncDialogBase } from '../launch-sync-dialog-base'

import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

export interface ConcludeTestSettings {
  restoreSettings: Array<LaunchRestoreSettings>
  successfulTest: boolean
  testNotes: string
  // Report variants the user asked for, in the order they should be generated.
  // Empty -> skip the report side-flow entirely.
  reportTypes: ReportType[]
  // Email addresses that should receive the report as a PDF attachment when
  // it's generated.  Empty / omitted -> no email is sent.
  reportRecipients: string[]
}

interface StatusInfo {
  label: string
  inProgress: boolean
}

interface SelectionView {
  toCancel: Application[]
  toConclude: Application[]
  allCancel: boolean
  hasMixed: boolean
  cancelledAppNames: string
  concludedAppNames: string
}

const EMPTY_STATUS_INFO: StatusInfo = { label: '', inProgress: false }

@Component({
  selector: 'conclude-test-dialog',
  templateUrl: './conclude-test-dialog.component.html',
  styleUrls: ['./conclude-test-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConcludeTestDialogComponent extends LaunchSyncDialogBase implements DoCheck {
  @Output() conclude = new EventEmitter<ConcludeTestSettings>()
  protectRecoveryPoints: boolean = false
  generateSummaryReport: boolean = false
  generateDetailedReport: boolean = false
  reportRecipients: string[] = []
  successfulTest: string = ''
  testNotes: string = ''

  readonly isValidEmail = (value: string) => emailAddressIsValid(value)
  private underTestApplicationIds = new Set<string>()
  private principalSubscription: Subscription = null

  // Snapshot of the cancel-vs-conclude bucketing for the current CD pass.
  // Recomputed each tick in ngDoCheck so the dialog title, disclaimer copy,
  // submit-button label, and outcome-step visibility all read from the same
  // bucketing — they can never disagree because they're computed atomically.
  private selectionView: SelectionView = {
    toCancel: [],
    toConclude: [],
    allCancel: false,
    hasMixed: false,
    cancelledAppNames: '',
    concludedAppNames: '',
  }

  // Per-app status info computed once per CD tick (in ngDoCheck) and looked up
  // by the template. Prevents the per-row template from calling
  // `applicationStatusInfo()` multiple times per row per CD (CLAUDE.md
  // pitfall #13) — Map.get is O(1) so even 3 binding lookups reuse one result.
  private statusInfoCache = new Map<Application, StatusInfo>()

  // Per Jira comment: when at least one candidate app is mid-restore, offer a
  // toggle to hide the in-progress rows so the user can focus on the apps
  // they're actually concluding. The toggle is hidden when no app would be
  // affected (no in-progress candidates).
  showOnlyCompleted: boolean = false

  // Esc = Cancel. Listens at the document level so it fires regardless of
  // where focus is inside the modal. Short-circuits while a conclude is in
  // flight so an accidental Esc can't tear down the dialog mid-request.
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isTopmostModal()) return
    if (this.launching) return
    event.preventDefault()
    this.onCancelClick()
  }

  constructor(
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  ngOnInit() {
    super.ngOnInit()
    // Only default the report on when the account is actually entitled to recovery reports
    this.generateSummaryReport = this.recoveryReportsEnabled
    this.generateDetailedReport = this.recoveryReportsEnabled
    // The report now defaults on, so seed the recipients with the logged-in
    // user's email — sent somewhere without extra typing, still editable. The
    // principal's subjectId is an opaque user id, not an email, so resolve the
    // address off the matching account subject (same lookup the profile uses).
    this.principalSubscription = this.apiService
      .getAuthenticatedPrincipal()
      .pipe(
        switchMap((principal) =>
          this.apiService
            .listAccountSubjects(this.accountService.accountId)
            .pipe(map((subjects) => ({ principal, subjects })))
        )
      )
      .subscribe({
        next: ({ principal, subjects }) => {
          const currentSubject = subjects.find(
            (s) =>
              s.subjectId === principal.subjectId || principal.effectiveAccountSubjectIds?.includes(s.accountSubjectId)
          )
          const email = currentSubject?.email
          if (email && this.isValidEmail(email) && this.reportRecipients.length === 0) {
            this.reportRecipients = [email]
            this.cdr.markForCheck()
          }
        },
        // Non-fatal — leave recipients empty if the lookup fails.
        error: () => {},
      })
  }

  ngOnDestroy() {
    this.principalSubscription?.unsubscribe()
    super.ngOnDestroy()
  }

  // The two report cards are independent — summary alone, detailed alone, both,
  // or neither. The backend renders a PDF per requested type and returns a single
  // download: the PDF itself for one type, a zip of both for two.
  get selectedReportTypes(): ReportType[] {
    const types: ReportType[] = []
    if (this.generateSummaryReport) types.push('summary')
    if (this.generateDetailedReport) types.push('detailed')
    return types
  }

  get anyReportSelected(): boolean {
    return this.generateSummaryReport || this.generateDetailedReport
  }

  get reportNote(): string {
    if (this.generateSummaryReport && this.generateDetailedReport) {
      return 'Both reports generate and download when the test concludes.'
    }
    if (this.anyReportSelected) {
      return 'The report generates and downloads when the test concludes.'
    }
    return 'No report will be generated when the test concludes.'
  }

  // Primary-button click — submit the conclude request. Both variants commit on
  // first click; the early-stop view is itself the confirmation, and the normal
  // flow's multi-step form (outcome selection, notes, retention) is a deliberate
  // enough action that a second-click prompt only adds friction.
  onConcludeClick() {
    // Concluding starts its own restore back to standby, and that latch lands while this
    // dialog is still open. Freeze first so a committed submit can't rewrite itself into the
    // cancel variant (title, disclaimers, dropped outcome/report steps) mid-request.
    this.freezeForSubmit()
    this.emitConclude()
  }

  private emitConclude() {
    this.conclude.emit({
      restoreSettings: this.selectedApplications.map((app) => {
        const setting: LaunchRestoreSettings = { application: app }
        if (this.protectRecoveryPoints) {
          // adaptRestoreRequest reads protectRecoveryPoint (singular) from the
          // payload — bracket access is intentional, the dialog emits a value
          // that the RestoreRequest adapter consumes.
          ;(setting as unknown as Record<string, unknown>)['protectRecoveryPoint'] = this.protectRecoveryPoints
        }
        // Tag the cancel-bound rows so the parent dispatch can record the
        // cancel note + false outcome for them, regardless of what outcome the
        // user picked for the concluded apps in the same batch.
        if (this.statusInfoFor(app).inProgress) {
          setting.cancelledMidRestore = true
        }
        return setting
      }),
      // Default outcome/notes for the conclude-bound rows. The parent
      // (`concludeTestFailover`) reads `cancelledMidRestore` per row and
      // overrides these for cancel-bound rows.
      successfulTest: this.allSelectedWillBeCancelled ? false : this.successfulTest === 'true',
      testNotes: this.allSelectedWillBeCancelled ? 'Test cancelled before the restore completed.' : this.testNotes,
      // An all-cancel batch has no recovery to report on, so no variant rides through.
      reportTypes: this.allSelectedWillBeCancelled ? [] : this.selectedReportTypes,
      reportRecipients: this.anyReportSelected && !this.allSelectedWillBeCancelled ? this.reportRecipients : [],
    })
  }

  ngDoCheck(): void {
    // Holding both caches holds every cancel-vs-conclude binding: title, disclaimers,
    // outcome + report steps, submit-button variant, and the per-row status pills.
    if (this.frozenForSubmit) return
    this.rebuildStatusInfoCache()
    this.selectionView = this.computeSelectionView()
  }

  // Refresh per-app status info once per CD tick. Both the template (per-row
  // pill) and `computeSelectionView` read from this cache; together they
  // collapse what was previously 3+ `applicationStatusInfo()` calls per row
  // per CD into a single computation per app per tick.
  private rebuildStatusInfoCache(): void {
    this.statusInfoCache.clear()
    const apps = this._applications ?? []
    for (const app of apps) {
      this.statusInfoCache.set(app, this.applicationStatusInfo(app))
    }
  }

  // Template entry point — returns the cached status info or a safe default
  // for the (transient) window before ngDoCheck first runs.
  statusInfoFor(app: Application): StatusInfo {
    return this.statusInfoCache.get(app) ?? EMPTY_STATUS_INFO
  }

  // Single bucketing pass so every cancel-vs-conclude binding agrees with the
  // per-row status pills the user sees. Stored on `selectionView` and only
  // refreshed in ngDoCheck (i.e. once per CD tick) — guards against the title
  // and submit-button label disagreeing if a publish-subject markForCheck
  // arrived mid-evaluation.
  private computeSelectionView(): SelectionView {
    const selected = this.selectedApplications
    const toCancel = selected.filter((app) => this.statusInfoFor(app).inProgress)
    const toConclude = selected.filter((app) => !this.statusInfoFor(app).inProgress)
    return {
      toCancel,
      toConclude,
      allCancel: selected.length > 0 && toConclude.length === 0,
      hasMixed: toCancel.length > 0 && toConclude.length > 0,
      cancelledAppNames: toCancel.map((app) => app.name).join(', '),
      concludedAppNames: toConclude.map((app) => app.name).join(', '),
    }
  }

  get selectedToCancel(): Application[] {
    return this.selectionView.toCancel
  }

  get selectedToConclude(): Application[] {
    return this.selectionView.toConclude
  }

  get allSelectedWillBeCancelled(): boolean {
    return this.selectionView.allCancel
  }

  get hasMixedSelection(): boolean {
    return this.selectionView.hasMixed
  }

  get dialogTitle(): string {
    return this.selectionView.allCancel
      ? 'Cancel test for selected applications'
      : 'Conclude test for selected applications'
  }

  // Outcome dropdown is only required when at least one selected app is past
  // its restore — cancel-only submissions skip the outcome step
  // entirely and use the fixed cancelled-test note.
  get outcomeRequired(): boolean {
    return this.selectionView.toConclude.length > 0
  }

  get cancelledAppNames(): string {
    return this.selectionView.cancelledAppNames
  }

  get concludedAppNames(): string {
    return this.selectionView.concludedAppNames
  }

  get canSubmit(): boolean {
    if (this.selectedApplications.length === 0) return false
    if (!this.outcomeRequired) return true
    return this.successfulTest === 'true' || this.successfulTest === 'false'
  }

  // Per-app status shown on the right of each row. Apps whose initial
  // restore for this test cycle hasn't completed yet display "Restore in progress…";
  // completed apps display "Restored {n} ago" so the user can see what they're about
  // to act on. The flow on submit ("CANCEL TEST" / "CONCLUDE TEST") follows from the
  // same signal and is surfaced via the disclaimer + button label. Templates
  // should bind to `statusInfoFor(app)` instead of calling this directly so the
  // result hits the per-CD cache.
  private applicationStatusInfo(app: Application): StatusInfo {
    if (app.currentlyRestoring) {
      return { label: 'Restore in progress…', inProgress: true }
    }
    if (app.lastRestoreComplete) {
      const verb =
        app.lastRestoreSuccess && app.lastRestoreSuccess >= (app.lastRestoreFailure ?? new Date(0))
          ? 'Restored'
          : 'Restore failed'
      return { label: `${verb} ${timeAgoString(app.lastRestoreComplete)}`, inProgress: false }
    }
    return { label: 'Awaiting restore…', inProgress: true }
  }

  protected applicationIsCandidate(application: Application) {
    // After concluding the test, the application syncPhase reverts to STANDBY before
    // the dialog is closed.  That causes some jumping around on the screen.
    // To fix this, we'll cache the IDs of applications that were ever in
    // FAILOVER_TEST within this dialog, and include them in the candidate
    // applications.
    const inTest = application.syncPhase === SyncPhaseDto.FAILOVER_TEST
    if (inTest) {
      this.underTestApplicationIds.add(application.applicationId)
    }
    const isCandidate = inTest || this.underTestApplicationIds.has(application.applicationId)
    if (!isCandidate) return false
    // Azure can't cancel an in-flight restore (backend limitation, matches the
    // page-level `isAzureRestoreInFlight` gate in application.component). Hide
    // the row entirely so the user isn't offered a "cancel" they can't perform.
    // Remove this filter once Azure mid-restore cancellation is supported.
    if (application.isAzure && application.currentlyRestoring) return false
    // "Show only completed" filter hides rows whose restore is still
    // in flight so the user can focus on the apps they're actually concluding.
    // The toggle itself is only surfaced when at least one candidate is in
    // progress (see `hasAnyInProgressCandidate`).
    if (this.showOnlyCompleted && this.statusInfoFor(application).inProgress) {
      return false
    }
    return true
  }

  // Drives the toggle's visibility — only worth offering when there's
  // actually an in-progress row to hide. Walks the unfiltered candidates so
  // the user can still toggle once an app finishes restoring while open.
  get hasAnyInProgressCandidate(): boolean {
    for (const app of this._applications ?? []) {
      const inTest = app.syncPhase === SyncPhaseDto.FAILOVER_TEST
      const isCandidate = inTest || this.underTestApplicationIds.has(app.applicationId)
      if (isCandidate && this.statusInfoFor(app).inProgress) {
        return true
      }
    }
    return false
  }

  get retentionPolicyUrl() {
    return this.uiService.buildRetentionPolicyUrl(this.accountService.accountId)
  }

  get productName() {
    return this.uiService.productName
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_TEST
  }
}
